import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from '../components/Common/Button';
import { CustomJwtPayload } from '../components/Sidebar';
import {
  setIsLoggedIn,
  setUpdateStudentDetails,
  setUpdateStudentState,
  setUserId
} from '../redux/Slices/AuthSlice';
import { useAppSelector } from '../redux/store/hooks';
import {
  useLazyGetPrePopulatedStudentDataQuery,
  useVerifyOtpMutation
} from '../services/auth/authApi';
import { setItem } from '../utils/storage';
let currentIndex = 0;
function Otp() {
  const [showError, setShowError] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(new Array(4).fill(''));
  const isOtpIncomplete = otp.some(digit => digit === '');
  const [verifyOtp, { isLoading, data, isError }] = useVerifyOtpMutation();
  const [getPrepopulatedStudentData] = useLazyGetPrePopulatedStudentDataQuery();
  const { studentDetails } = useAppSelector(state => state.root.auth);
  const { emailId } = studentDetails;
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeOTPIndex, setActiveOTPIndex] = useState<number>(0);
  const [minutes, setMinutes] = useState(2);
  const [seconds, setSeconds] = useState(0);
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const onChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    const isNumeric = /^\d*$/.test(value);
    if (!isNumeric) {
      return;
    }
    const newOtp: string[] = [...otp];
    newOtp[currentIndex] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (!value) {
      setActiveOTPIndex(currentIndex - 1);
    } else {
      setActiveOTPIndex(currentIndex + 1);
    }
    setShowError(false);
  };

  const keyDownHandler = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ): void => {
    currentIndex = index;
    if (event.key === 'Backspace') {
      const newOtp: string[] = [...otp];
      if (newOtp[currentIndex] !== '') {
        newOtp[currentIndex] = '';
        setOtp(newOtp);
        setActiveOTPIndex(currentIndex);
      } else {
        newOtp[currentIndex] = '';
        setOtp(newOtp);
        setActiveOTPIndex(currentIndex - 1);
      }
    }
  };

  useEffect(() => {
    // Timer to decrement time every second
    const timer = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(timer);
        } else {
          setMinutes(prevMinutes => prevMinutes - 1);
          setSeconds(59);
        }
      } else {
        setSeconds(prevSeconds => prevSeconds - 1);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [minutes, seconds]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeOTPIndex]);

  const getPrepopulatedStudentsData = async () => {
    try {
      const response = await getPrepopulatedStudentData({}).unwrap();
      if (response.status === 'success') {
        const {
          name,
          college,
          mobile,
          yearOfCommencement,
          yearOfPassing,
          qualification,
          department,
          gender,
          collegeRollNo,
          academicDetails,
          skipMobileNumValidation
        } = response.data;

        const updatedStudentDetails = {
          ...studentDetails,
          name: name || '',
          college: college || '',
          mobile: mobile || '',
          yearOfCommencement: yearOfCommencement || 0,
          yearOfPassing: yearOfPassing || '',
          qualification: qualification || '',
          department: department || '',
          gender: gender || '',
          collegeRollNo: collegeRollNo || '',
          academicDetails: academicDetails || {},
          skipMobileNumValidation: skipMobileNumValidation || false
        };
        dispatch(setUpdateStudentState(updatedStudentDetails));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const body = {
        emailId,
        otp: parseInt(otp.join(''))
      };
      const res = await verifyOtp(body).unwrap();

      const decodedToken: CustomJwtPayload = jwtDecode(res.token);
      const role = decodedToken.role;
      setItem({ key: 'dashboards', value: res.dashboards });
      setItem({ key: 'role', value: `${role}` });

      if (decodedToken?.userType === 6001 && !res.isprofilecompleted) {
        setItem({ key: 'token', value: res.token });
        await getPrepopulatedStudentsData();
      }

      if (res.isprofilecompleted) {
        setItem({ key: 'token', value: res.token });
        setItem({ key: 'isProfileCompleted', value: 'true' });
        dispatch(setUserId(decodedToken?.userId));
        dispatch(setIsLoggedIn(true));
        navigateBasedOnRole(role);
        dispatch(
          setUpdateStudentDetails({
            key: 'isProfileCompleted',
            value: res.isprofilecompleted
          })
        );
      } else {
        dispatch(setUserId(decodedToken?.userId));
        dispatch(
          setUpdateStudentDetails({
            key: 'isProfileCompleted',
            value: res.isprofilecompleted
          })
        );
        setItem({ key: 'token', value: res.token });
        setItem({ key: 'isFirstTimeLogin', value: 'true' });
        navigate('/details/name');
      }
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(error?.data?.message);
      } else {
        toast.error('Unknown error');
      }
    }
  };

  const navigateBasedOnRole = (role: number) => {
    if (role == 1003) {
      navigate('/datareviewercms');
    } else if (role == 1004) {
      navigate('/dataentryspecialistcms');
    } else if (role == 1002) {
      navigate('/tenant-managment');
      setItem({ key: 'activeItem', value: 'Tenant Managment' });
    } else {
      navigate('/analytics');
      setItem({ key: 'activeItem', value: 'Dashboard' });
    }
  };

  const onKeyDownHandler = (e: any) => {
    if (e.key === 'Enter') {
      handleVerifyOtp();
    }
  };

  const errorMessage = (error: any) => {
    if (showError && otp.join('') !== '') {
      if (error) {
        toast.error(error?.data?.error);
        return error?.data?.error;
      } else {
        return 'Unknown error';
      }
    }
  };

  return (
    <div className='relative flex min-h-screen w-full items-center justify-center bg-[#F5F9FF]'>
      <div className='z-[999] flex w-3/4 max-w-screen-md flex-col items-center justify-center rounded-2xl border bg-[#FFFFFF] p-4 md:w-[50%] lg:w-[50%]'>
        <h1 className='font-manrope my-4 text-center text-4xl font-semibold tracking-wider text-primary'>
          Freshers Today
        </h1>

        <div className='z-50 flex h-full w-full flex-col items-center justify-center'>
          <div className='mt-8 flex flex-row justify-center gap-8'>
            <div className='flex flex-col items-center'>
              <div className='flex flex-col items-center'>
                {/* <img src={logo} alt='ft' className='mb-2' /> */}
                <p className='font-avenir mr-1 text-center text-base font-medium text-[#3C424C]'>
                  Enter OTP shared on email{' '}
                  <span className='spanclasses'>{emailId}</span>
                </p>
                <div>
                  <button
                    onClick={() => {
                      navigate('/login');
                    }}
                  ></button>
                </div>
              </div>
              <div className='flex items-center justify-center '>
                {otp.map((_, index) => (
                  <>
                    <input
                      ref={index === activeOTPIndex ? inputRef : null}
                      type='tel'
                      className='m-2 mb-4 h-8 w-8 rounded-md border border-[#dee4ee] bg-white text-center text-xl font-semibold outline-none'
                      onChange={event => {
                        onChangeHandler(event);
                      }}
                      onKeyDown={event => {
                        keyDownHandler(event, index);
                        onKeyDownHandler(event);
                      }}
                      value={otp[index]}
                    />
                  </>
                ))}
              </div>
              {isError ? (
                <h4 className='text-secondaryColor '>{errorMsg}</h4>
              ) : (
                data?.message && <p className='text-[green]'>{data.message}</p>
              )}
              <div className='mb-5 mt-3'>
                <Button
                  disabled={isOtpIncomplete || data?.message}
                  isLoading={isLoading}
                  label='Verify OTP'
                  handleClickBtn={handleVerifyOtp}
                  variant='contained'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Otp;
